import { yupResolver } from "@hookform/resolvers/yup"
import { format, parse } from "date-fns"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { useMutation } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import ContactBoatPicker from "src/components/ContactBoatPicker"
import PickerSubmitError from "src/components/ContactBoatPicker/PickerSubmitError"
import {
  emptyContact,
  emptyContactBoat,
} from "src/components/ContactBoatPicker/emptyShapes"
import parseResolveError from "src/components/ContactBoatPicker/parseResolveError"
import useContactBoatPicker, {
  PANEL_ARCHIVED,
  PANEL_NONE,
} from "src/components/ContactBoatPicker/useContactBoatPicker"
import Form from "src/components/Form"

import { createShortTermWaitlistEntry } from "src/api/Waitlist/shortTerm"

import {
  FirstDayInput,
  LastDayInput,
  SpaceTypeSelector,
  SpecialRequestInput,
} from "./inputs"
import { schema } from "./schema"

const ShortTermWaitlistForm = ({
  firstDay,
  lastDay,
  spaceType,
  multiDockage,
}) => {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      first_day: firstDay ? parse(firstDay, "MM/dd/yyyy", new Date()) : null,
      last_day: lastDay ? parse(lastDay, "MM/dd/yyyy", new Date()) : null,
      contact: emptyContact,
      contact_id: "",
      contact_boat: emptyContactBoat,
      contact_boat_id: "",
      special_request: "",
      space_type: spaceType,
    },
  })

  return (
    <div data-testid="dockmaster-short-term-waitlist-entry-form">
      <FormProvider {...methods}>
        <ShortTermWaitlistFormBody multiDockage={multiDockage} />
      </FormProvider>
    </div>
  )
}

const ShortTermWaitlistFormBody = ({ multiDockage }) => {
  const { marinaSlug } = useMarina()
  const { handleSubmit, watch } = useFormContext()
  const picker = useContactBoatPicker()
  const [contactId, contactBoatId] = watch(["contact_id", "contact_boat_id"])
  const [pickerSubmitError, setPickerSubmitError] = useState(null)

  const {
    mutate: createWaitlist,
    isLoading: loadingCreateWaitlist,
    isSuccess: successCreatingWaitlist,
    isError: errorCreatingWaitlist,
    reset: resetCreateWaitlistMutation,
  } = useMutation(
    (data) => createShortTermWaitlistEntry({ marinaSlug, data }),
    {
      onSuccess: (response) => {
        window.location.href = response.redirect_to
      },
    }
  )

  const onSubmit = async (formData) => {
    setPickerSubmitError(null)
    try {
      const { contactId: resolvedContactId, contactBoatId: resolvedBoatId } =
        await picker.resolveSelection(formData)
      const params = {
        first_day: format(formData.first_day, "MM/dd/yyyy"),
        last_day: format(formData.last_day, "MM/dd/yyyy"),
        special_request: formData.special_request.trim(),
        space_type: formData.space_type,
        contact_id: resolvedContactId,
        contact_boat_id: resolvedBoatId,
      }
      createWaitlist(params)
    } catch (error) {
      const parsed = parseResolveError(error)
      if (parsed) setPickerSubmitError(parsed)
    }
  }

  useEffect(() => {
    const subscription = watch(() => {
      if (errorCreatingWaitlist) resetCreateWaitlistMutation()
      setPickerSubmitError(null)
    })
    return () => subscription.unsubscribe()
  }, [watch, errorCreatingWaitlist, resetCreateWaitlistMutation])

  const submitDisabled =
    loadingCreateWaitlist ||
    successCreatingWaitlist ||
    picker.isWorking ||
    picker.panel === PANEL_ARCHIVED ||
    (picker.panel === PANEL_NONE && (!contactId || !contactBoatId))

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ContactBoatPicker handle={picker} />
      {pickerSubmitError ? (
        <div className="pt-2">
          <PickerSubmitError submitError={pickerSubmitError} />
        </div>
      ) : null}
      <hr />
      <div className="flex flex-col space-y-4">
        <div className="flex w-full flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0">
          <FirstDayInput />
          <LastDayInput />
        </div>
        {multiDockage ? <SpaceTypeSelector /> : null}
        <SpecialRequestInput />
        <Button
          variant="primary"
          type="submit"
          isLoading={
            loadingCreateWaitlist || successCreatingWaitlist || picker.isWorking
          }
          disabled={submitDisabled}
        >
          Add to Waitlist
        </Button>
        {errorCreatingWaitlist ? (
          <div className="w-full text-center">
            <Form.Error>
              There was an error creating the waitlist entry. Please try again
              later or contact support at mayday@dockwa.com.
            </Form.Error>
          </div>
        ) : null}
      </div>
    </form>
  )
}

ShortTermWaitlistFormBody.propTypes = {
  multiDockage: PropTypes.bool,
}

ShortTermWaitlistForm.propTypes = {
  firstDay: PropTypes.string,
  lastDay: PropTypes.string,
  spaceType: PropTypes.oneOf(["dock_or_mooring", "dock", "mooring"]),
  multiDockage: PropTypes.bool,
}

export default ShortTermWaitlistForm
